import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  type FC,
  type ReactNode,
} from 'react';
import { Spin } from 'antd';
import type {
  SiteContent,
  BlogPost,
  Project,
  Experience,
  Testimonial,
  Product,
} from '@/types';
import defaultSiteContent from '@/data/site-content.json';

/* ------------------------------------------------------------------ */
/*  In-memory content cache (survives re-renders, cleared on reload)  */
/* ------------------------------------------------------------------ */

const cache = new Map<string, unknown>();

async function fetchFromApi<T>(collection: string): Promise<T> {
  if (cache.has(collection)) return cache.get(collection) as T;

  const res = await fetch(`/api/content/${collection}`);
  if (!res.ok) throw new Error(`Failed to fetch ${collection}: ${res.status}`);
  const { data } = await res.json();
  cache.set(collection, data);
  return data as T;
}

/** Clear one or all cached collections (useful after admin saves) */
export function invalidateContent(collection?: string) {
  if (collection) cache.delete(collection);
  else cache.clear();
}

/* ------------------------------------------------------------------ */
/*  Site-content context  (loaded once, blocks render until ready)     */
/* ------------------------------------------------------------------ */

const SiteContentContext = createContext<SiteContent | null>(null);

export function useSiteContent(): SiteContent {
  const ctx = useContext(SiteContentContext);
  if (!ctx) throw new Error('useSiteContent must be used within ContentProvider');
  return ctx;
}

/* ------------------------------------------------------------------ */
/*  Generic collection hook                                           */
/* ------------------------------------------------------------------ */

export function useContent<T>(name: string): {
  data: T | null;
  loading: boolean;
  error: string | null;
} {
  const [data, setData] = useState<T | null>(
    cache.has(name) ? (cache.get(name) as T) : null,
  );
  const [loading, setLoading] = useState(!cache.has(name));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cache.has(name)) {
      setData(cache.get(name) as T);
      setLoading(false);
      return;
    }

    let cancelled = false;
    fetchFromApi<T>(name)
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError((e as Error).message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [name]);

  return { data, loading, error };
}

/* ------------------------------------------------------------------ */
/*  Typed collection hooks                                            */
/* ------------------------------------------------------------------ */

const EMPTY_BLOG: BlogPost[] = [];
const EMPTY_PROJECTS: Project[] = [];
const EMPTY_EXPERIENCES: Experience[] = [];
const EMPTY_TESTIMONIALS: Testimonial[] = [];
const EMPTY_PRODUCTS: Product[] = [];

export function useBlogPosts() {
  const { data, loading, error } = useContent<BlogPost[]>('blog');
  const blogPosts = useMemo(
    () => data?.filter((p) => p.status === 'published') ?? EMPTY_BLOG,
    [data],
  );
  return { blogPosts, allBlogPosts: data ?? EMPTY_BLOG, loading, error };
}

export function useProjects() {
  const { data, loading, error } = useContent<Project[]>('projects');
  return { projects: data ?? EMPTY_PROJECTS, loading, error };
}

export function useExperiences() {
  const { data, loading, error } = useContent<Experience[]>('experience');
  return { experiences: data ?? EMPTY_EXPERIENCES, loading, error };
}

export function useTestimonials() {
  const { data, loading, error } = useContent<Testimonial[]>('testimonials');
  return { testimonials: data ?? EMPTY_TESTIMONIALS, loading, error };
}

export function useProducts() {
  const { data, loading, error } = useContent<Product[]>('products');
  const products = useMemo(
    () => data?.filter((p) => p.status === 'active') ?? EMPTY_PRODUCTS,
    [data],
  );
  return { products, allProducts: data ?? EMPTY_PRODUCTS, loading, error };
}

/* ------------------------------------------------------------------ */
/*  Provider                                                          */
/* ------------------------------------------------------------------ */

export const ContentProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [siteContent, setSiteContent] = useState<SiteContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only block on site-content (needed for navbar/footer/hero).
    // All other collections are fetched lazily by individual pages.
    fetchFromApi<SiteContent>('site-content')
      .then((data) => {
        // Merge defaults for new fields not yet in API data
        const merged = { ...data };
        for (const key of Object.keys(defaultSiteContent) as (keyof SiteContent)[]) {
          if (merged[key] && typeof merged[key] === 'object' && !Array.isArray(merged[key])) {
            merged[key] = { ...(defaultSiteContent as SiteContent)[key], ...merged[key] } as never;
          }
        }
        setSiteContent(merged);
        setLoading(false);
      })
      .catch((e) => {
        setError((e as Error).message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'var(--color-bg, #0a0a0a)',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error || !siteContent) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'var(--color-bg, #0a0a0a)',
          color: 'var(--color-text, #e8e8e8)',
          gap: 16,
        }}
      >
        <h2 style={{ margin: 0 }}>Failed to load content</h2>
        <p style={{ opacity: 0.6 }}>{error || 'Unknown error'}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '8px 24px',
            borderRadius: 6,
            border: '1px solid rgba(176,150,125,0.3)',
            background: 'rgba(176,150,125,0.1)',
            color: 'var(--color-text, #e8e8e8)',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <SiteContentContext.Provider value={siteContent}>
      {children}
    </SiteContentContext.Provider>
  );
};
